import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Annotation, ElementMapEntry } from '@/types/sem';

interface AnnotatedScreenshotProps {
  screenshotPath: string | null;
  elementMap: ElementMapEntry[];
  annotations: Annotation[];
  foldY: number;
  viewportWidth: number;
}

/**
 * Renders a page screenshot with absolutely-positioned annotation zones
 * and a fold line. Screenshot is loaded lazily from Supabase Storage.
 */
export function AnnotatedScreenshot({
  screenshotPath,
  elementMap,
  annotations,
  foldY,
  viewportWidth,
}: AnnotatedScreenshotProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageNatural, setImageNatural] = useState<{ w: number; h: number } | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track container width for scaling annotation zones
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Load screenshot URL from Supabase Storage
  useEffect(() => {
    if (!screenshotPath || !supabase) return;
    const { data } = supabase.storage.from('audit-screenshots').getPublicUrl(screenshotPath);
    if (data?.publicUrl) {
      setImageUrl(data.publicUrl);
    }
  }, [screenshotPath]);

  // Scale factor: container pixel width / original viewport width
  const scale = containerWidth > 0 && viewportWidth > 0
    ? containerWidth / viewportWidth
    : 1;

  function resolveZone(annotation: Annotation): { x: number; y: number; w: number; h: number } | null {
    if (annotation.severity === 'addition' && annotation.suggested_position) {
      // Position based on suggested_position
      const positions: Record<string, number> = {
        below_hero: foldY + 20,
        above_footer: (imageNatural?.h || 2000) - 120,
        after_content: (() => {
          const contentEls = elementMap.filter(e => e.role === 'content');
          if (contentEls.length > 0) {
            const last = contentEls[contentEls.length - 1];
            return last.bbox.y + last.bbox.h + 10;
          }
          return foldY + 100;
        })(),
      };
      const y = positions[annotation.suggested_position] || foldY + 20;
      return { x: 20, y, w: viewportWidth - 40, h: 60 };
    }

    if (!annotation.target_element) return null;

    const target = annotation.target_element;
    const matchRules: Record<string, (e: ElementMapEntry) => boolean> = {
      h1: (e) => e.tag === 'H1',
      h2: (e) => e.tag === 'H2',
      cta: (e) => e.role === 'cta',
      form: (e) => e.role === 'form',
      trust_signals: (e) => e.role === 'trust',
      social_proof: (e) => e.role === 'social_proof',
      hero_image: (e) => e.tag === 'IMG' && e.bbox.y < foldY,
      body_content: (e) => e.role === 'content',
    };

    const matchFn = matchRules[target];
    if (!matchFn) return null;

    const matches = elementMap.filter(matchFn);
    if (matches.length === 0) return null;

    // Special cases
    if (target === 'hero_image') {
      // Largest by area
      const largest = matches.reduce((a, b) =>
        (a.bbox.w * a.bbox.h) > (b.bbox.w * b.bbox.h) ? a : b
      );
      return largest.bbox;
    }

    if (target === 'trust_signals' || target === 'social_proof' || target === 'body_content') {
      // Union bbox
      const minX = Math.min(...matches.map(m => m.bbox.x));
      const minY = Math.min(...matches.map(m => m.bbox.y));
      const maxX = Math.max(...matches.map(m => m.bbox.x + m.bbox.w));
      const maxY = Math.max(...matches.map(m => m.bbox.y + m.bbox.h));
      return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }

    if (target === 'cta') {
      // Prefer above-fold match
      const aboveFold = matches.filter(m => m.bbox.y < foldY);
      return (aboveFold.length > 0 ? aboveFold[0] : matches[0]).bbox;
    }

    // Default: first match
    return matches[0].bbox;
  }

  if (!screenshotPath) return null;

  return (
    <div
      className="audit-screenshot-wrap"
      ref={containerRef}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt="Page screenshot"
          onLoad={(e) => {
            const img = e.target as HTMLImageElement;
            setImageNatural({ w: img.naturalWidth, h: img.naturalHeight });
          }}
        />
      )}

      {/* Fold line */}
      <div
        className="audit-fold-line"
        style={{ top: `${foldY * scale}px` }}
      >
        <span className="audit-fold-label">FOLD</span>
      </div>

      {/* Annotation zones */}
      {annotations.map((ann) => {
        const zone = resolveZone(ann);
        if (!zone) return null;

        const severityClass = `audit-zone-${ann.severity}`;

        return (
          <div
            key={ann.zone_index}
            className={`audit-zone ${severityClass}`}
            style={{
              left: `${zone.x * scale}px`,
              top: `${zone.y * scale}px`,
              width: `${zone.w * scale}px`,
              height: `${zone.h * scale}px`,
            }}
          >
            <span className="audit-zone-badge">{ann.zone_index}</span>
            {ann.severity === 'addition' && (
              <span className="audit-zone-addition-label">+ RECOMMENDED</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
